/**
 * UI Store - Laumeris
 * Pinia store for UI state (sidebar, theme, etc.)
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

type Theme = 'light' | 'dark' | 'system'

export const useUIStore = defineStore('ui', () => {
  // State
  const sidebarCollapsed = ref(false)
  const sidebarMobileOpen = ref(false)
  const theme = ref<Theme>('system')
  const isMobile = ref(false)

  // Getters
  const isDark = computed(() => {
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme.value === 'dark'
  })

  // Actions
  const toggleSidebar = () => {
    if (isMobile.value) {
      sidebarMobileOpen.value = !sidebarMobileOpen.value
    } else {
      sidebarCollapsed.value = !sidebarCollapsed.value
    }
  }

  const closeMobileSidebar = () => {
    sidebarMobileOpen.value = false
  }

  const setTheme = (newTheme: Theme) => {
    theme.value = newTheme
    localStorage.setItem('theme', newTheme)
    updateDocumentTheme()
  }

  const toggleTheme = () => {
    const newTheme = isDark.value ? 'light' : 'dark'
    setTheme(newTheme)
  }

  const updateDocumentTheme = () => {
    if (isDark.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  const checkMobile = () => {
    isMobile.value = window.innerWidth < 1024
    if (isMobile.value) {
      sidebarCollapsed.value = true
    }
  }

  const initialize = () => {
    // Load theme from localStorage
    const storedTheme = localStorage.getItem('theme') as Theme | null
    if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
      theme.value = storedTheme
    }
    updateDocumentTheme()

    // Check mobile
    checkMobile()
    window.addEventListener('resize', checkMobile)

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (theme.value === 'system') {
        updateDocumentTheme()
      }
    })
  }

  return {
    // State
    sidebarCollapsed,
    sidebarMobileOpen,
    theme,
    isMobile,

    // Getters
    isDark,

    // Actions
    toggleSidebar,
    closeMobileSidebar,
    setTheme,
    toggleTheme,
    initialize
  }
})
